import authAxios from '../core/api/interceptors/authAxios';
import axiosInstance from '../core/api/interceptors/axiosInterceptor';
import { toast } from 'react-toastify';
import store from '../app/store/store'; // ✅ Import Redux store
import { setUser, clearUser } from '../app/store/slices/authSlice'; // ✅ Import auth actions
import { setCompanyInfo, clearCompanyInfo } from '../app/store/slices/companySlice'; // ✅ Import company actions

export const login = async (email, password) => {
  try {
    console.log('🔐 Login attempt for:', email);
    
    // ✅ Send request with credentials to allow HTTP-only cookie setting
    const response = await authAxios.post('Account/sign-in', 
      { email, password },
      { 
        withCredentials: true  // ✅ Critical: Allows server to set HTTP-only cookies
      }
    );
    
    console.log('✅ Login response:', response.data);
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Login failed');
    }
    
    const { payload } = response.data;
    
    // ✅ Extract user and company from payload
    const user = {
      id: payload.Email,
      email: payload.Email,
      name: payload.Name || email.split('@')[0],
      //role: payload.Role || 'user',
    };
    
    // ✅ Extract company info if it exists
    const company = payload.Company || null;
    
    // ✅ Store user data in localStorage (safe, no sensitive token)
    localStorage.setItem('user', JSON.stringify(user));
    
    // ✅ Store user in Redux
    store.dispatch(setUser(user));
    console.log('✅ User data stored in Redux:', user);
    
    // ✅ Store company info in Redux if available
    if (company) {
      store.dispatch(setCompanyInfo(company));
      console.log('🏢 Company info stored in Redux:', company);
    } else {
      console.warn('⚠️ No company info in login response');
    }
    
    toast.success(response.data.message || 'Login successful!');
    
    return { user, company }; // ✅ Return both user and company
  } catch (error) {
    console.error('❌ Login error:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      const errorMessage = errorData.message || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error(error.message || 'An unexpected error occurred');
    }
    
    throw error;
  }
};

export const logout = async () => {
  try {
    console.log('🚪 Initiating logout...');
    
    // ✅ Call backend to clear HTTP-only cookie
    await axiosInstance.post('Account/logout', {}, {
      withCredentials: true
    });
    
    console.log('✅ Server logout successful');
  } catch (error) {
    console.error('⚠️ Server logout failed:', error);
    // Continue with client-side cleanup even if server call fails
  } finally {
    // ✅ Clear client-side user data
    localStorage.removeItem('user');
    
    // ✅ Clear Redux state
    store.dispatch(clearUser());
    store.dispatch(clearCompanyInfo());
    
    // ✅ Reset all store slices (customers, products, invoices, etc.)
    const { resetStore } = require('../app/store/store');
    store.dispatch(resetStore());
    
    console.log('✅ Redux state and all slices cleared');
    
    console.log('✅ Client-side cleanup completed');
    
    // ✅ Redirect to login immediately without toast
    window.location.href = '/login';
  }
};

export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    
    if (!userStr) {
      return null;
    }

    const user = JSON.parse(userStr);
    return user;
  } catch (error) {
    console.error('❌ Error getting current user:', error);
    localStorage.removeItem('user');
    return null;
  }
};

export const isAuthenticated = () => {
  const user = getCurrentUser();
  return !!user; // Server will validate the HTTP-only cookie
};

// ✅ Initialize auth on app startup (restore user and try to get company)
export const initializeAuth = async () => {
  const user = getCurrentUser();
  
  if (user) {
    // Restore user to Redux
    store.dispatch(setUser(user));
    console.log('✅ User restored to Redux:', user);
    
    // Try to fetch store info from API (in case of page refresh)
    try {
      const storeInfo = await getStoreInfo();
      if (storeInfo) {
        store.dispatch(setCompanyInfo(storeInfo));
        console.log('🏪 Store info fetched and stored:', storeInfo);
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch store info on init:', error);
      // Not critical - user can still work, just no store info yet
    }
  }
};

export const register = async (userData) => {
  try {
    console.log('📝 Registration attempt');
    
    const response = await authAxios.post('Account/register', userData, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Registration failed');
    }
    
    toast.success(response.data.message || 'Registration successful!');
    return response.data;
  } catch (error) {
    console.error('❌ Registration error:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Registration failed');
    } else {
      toast.error('Registration failed. Please try again.');
    }
    
    throw error;
  }
};

export const forgotPassword = async (email) => {
  try {
    console.log('🔐 Forgot password request for:', email);
    
    const response = await authAxios.post('Account/forgot-password', { email }, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to send reset email');
    }
    
    toast.success(response.data.message || 'Password reset email sent successfully!');
    return response.data;
  } catch (error) {
    console.error('❌ Forgot password error:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to send reset email');
    } else {
      toast.error('Failed to send reset email. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Get company info from API (fallback for page refresh or when not in login response)
export const getCompanyInfo = async () => {
  try {
    console.log('🏢 Fetching company info from API...');
    
    const response = await axiosInstance.get('Account/company-info', {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to fetch company info');
    }
    
    const company = response.data.payload;
    
    // ✅ Store in Redux when fetched
    if (company) {
      store.dispatch(setCompanyInfo(company));
      console.log('🏢 Company info fetched and stored:', company);
    }
    
    return company;
  } catch (error) {
    console.error('❌ Error fetching company info:', error);
    throw error;
  }
};

// ✅ Get store info from API (current active endpoint)
export const getStoreInfo = async () => {
  try {
    console.log('🏪 Fetching store info from API...');
    
    const response = await axiosInstance.get('Account/store-info', {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to fetch store info');
    }
    
    const storeInfo = response.data.payload;
    
    // ✅ Store in Redux when fetched
    if (storeInfo) {
      store.dispatch(setCompanyInfo(storeInfo));
      console.log('🏪 Store info fetched and stored:', storeInfo);
    }
    
    return storeInfo;
  } catch (error) {
    console.error('❌ Error fetching store info:', error);
    throw error;
  }
};

// ✅ Get user profile from API
export const getUserProfile = async () => {
  try {
    console.log('👤 Fetching user profile from API...');
    
    const response = await axiosInstance.get('Account/profile', {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to fetch user profile');
    }
    
    const profile = response.data.payload;
    
    // ✅ Update localStorage and Redux when fetched
    if (profile) {
      const user = {
        id: profile.Id || profile.Email,
        email: profile.Email,
        name: profile.Name || profile.Username,
        username: profile.Username,
        phone: profile.Phone,
        avatar: profile.Avatar || profile.ProfileImage,
      };
      
      localStorage.setItem('user', JSON.stringify(user));
      store.dispatch(setUser(user));
      console.log('✅ User profile fetched and stored:', user);
      
      return user;
    }
    
    return profile;
  } catch (error) {
    console.error('❌ Error fetching user profile:', error);
    throw error;
  }
};

// ✅ Update user profile
export const updateUserProfile = async (profileData) => {
  try {
    console.log('📝 Updating user profile...');
    
    const response = await axiosInstance.put('Account/profile', profileData, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to update user profile');
    }
    
    const updatedProfile = response.data.payload;
    
    // ✅ Update localStorage and Redux after successful update
    if (updatedProfile) {
      const user = {
        id: updatedProfile.Id || updatedProfile.Email,
        email: updatedProfile.Email,
        name: updatedProfile.Name || updatedProfile.Username,
        username: updatedProfile.Username,
        phone: updatedProfile.Phone,
        avatar: updatedProfile.Avatar || updatedProfile.ProfileImage,
      };
      
      localStorage.setItem('user', JSON.stringify(user));
      store.dispatch(setUser(user));
      console.log('✅ User profile updated:', user);
    }
    
    toast.success(response.data.message || 'Profile updated successfully!');
    return updatedProfile;
  } catch (error) {
    console.error('❌ Error updating user profile:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to update profile');
    } else {
      toast.error('Failed to update profile. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Update company information
export const updateCompanyInfo = async (companyData) => {
  try {
    console.log('🏢 Updating company info...');
    
    const response = await axiosInstance.put('Account/company-info', companyData, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to update company information');
    }
    
    const updatedCompany = response.data.payload;
    
    // ✅ Update Redux after successful update
    if (updatedCompany) {
      store.dispatch(setCompanyInfo(updatedCompany));
      console.log('🏢 Company info updated:', updatedCompany);
    }
    
    toast.success(response.data.message || 'Company information updated successfully!');
    return updatedCompany;
  } catch (error) {
    console.error('❌ Error updating company info:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to update company information');
    } else {
      toast.error('Failed to update company information. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Upload user avatar/profile image
export const uploadUserAvatar = async (file) => {
  try {
    console.log('📸 Uploading user avatar...');
    
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post('Account/upload-avatar', formData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to upload avatar');
    }
    
    const avatarUrl = response.data.payload?.url || response.data.payload?.avatarUrl;
    
    // ✅ Update user in localStorage and Redux
    if (avatarUrl) {
      const currentUser = getCurrentUser();
      if (currentUser) {
        currentUser.avatar = avatarUrl;
        localStorage.setItem('user', JSON.stringify(currentUser));
        store.dispatch(setUser(currentUser));
        console.log('✅ Avatar updated:', avatarUrl);
      }
    }
    
    toast.success(response.data.message || 'Avatar uploaded successfully!');
    return avatarUrl;
  } catch (error) {
    console.error('❌ Error uploading avatar:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to upload avatar');
    } else {
      toast.error('Failed to upload avatar. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Change password
export const changePassword = async (oldPassword, newPassword) => {
  try {
    console.log('🔐 Changing password...');
    
    const response = await axiosInstance.post('Account/change-password', {
      oldPassword,
      newPassword,
    }, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to change password');
    }
    
    toast.success(response.data.message || 'Password changed successfully!');
    return response.data;
  } catch (error) {
    console.error('❌ Error changing password:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to change password');
    } else {
      toast.error('Failed to change password. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Reset password (with token from email)
export const resetPassword = async (token, newPassword) => {
  try {
    console.log('🔐 Resetting password with token...');
    
    const response = await authAxios.post('Account/reset-password', {
      token,
      newPassword,
    }, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to reset password');
    }
    
    toast.success(response.data.message || 'Password reset successfully!');
    return response.data;
  } catch (error) {
    console.error('❌ Error resetting password:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to reset password');
    } else {
      toast.error('Failed to reset password. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Verify email
export const verifyEmail = async (token) => {
  try {
    console.log('✉️ Verifying email...');
    
    const response = await authAxios.post('Account/verify-email', {
      token,
    }, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to verify email');
    }
    
    toast.success(response.data.message || 'Email verified successfully!');
    return response.data;
  } catch (error) {
    console.error('❌ Error verifying email:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to verify email');
    } else {
      toast.error('Failed to verify email. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Resend verification email
export const resendVerificationEmail = async (email) => {
  try {
    console.log('📧 Resending verification email...');
    
    const response = await authAxios.post('Account/resend-verification', {
      email,
    }, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to resend verification email');
    }
    
    toast.success(response.data.message || 'Verification email sent successfully!');
    return response.data;
  } catch (error) {
    console.error('❌ Error resending verification email:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to resend verification email');
    } else {
      toast.error('Failed to resend verification email. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Delete account
export const deleteAccount = async (password) => {
  try {
    console.log('🗑️ Deleting account...');
    
    const response = await axiosInstance.delete('Account/delete-account', {
      data: { password },
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to delete account');
    }
    
    // ✅ Clear all data after account deletion
    localStorage.clear();
    store.dispatch(clearUser());
    store.dispatch(clearCompanyInfo());
    
    toast.success(response.data.message || 'Account deleted successfully');
    
    // ✅ Redirect to login
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
    
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting account:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to delete account');
    } else {
      toast.error('Failed to delete account. Please try again.');
    }
    
    throw error;
  }
};

// ✅ Check if username is available
export const checkUsernameAvailability = async (username) => {
  try {
    console.log('🔍 Checking username availability...');
    
    const response = await authAxios.get(`Account/check-username/${username}`, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      return false;
    }
    
    return response.data.payload?.available || false;
  } catch (error) {
    console.error('❌ Error checking username:', error);
    return false;
  }
};

// ✅ Check if email is available
export const checkEmailAvailability = async (email) => {
  try {
    console.log('🔍 Checking email availability...');
    
    const response = await authAxios.get(`Account/check-email/${email}`, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      return false;
    }
    
    return response.data.payload?.available || false;
  } catch (error) {
    console.error('❌ Error checking email:', error);
    return false;
  }
};

// ✅ Get account activity/audit logs
export const getAccountActivity = async (page = 1, limit = 20) => {
  try {
    console.log('📊 Fetching account activity...');
    
    const response = await axiosInstance.get('Account/activity', {
      params: { page, limit },
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to fetch account activity');
    }
    
    return response.data.payload;
  } catch (error) {
    console.error('❌ Error fetching account activity:', error);
    throw error;
  }
};

// ✅ Enable/Disable two-factor authentication
export const toggleTwoFactorAuth = async (enable, password) => {
  try {
    console.log(`🔐 ${enable ? 'Enabling' : 'Disabling'} two-factor authentication...`);
    
    const response = await axiosInstance.post('Account/two-factor-auth', {
      enable,
      password,
    }, {
      withCredentials: true
    });
    
    if (response.data.status !== 'Success' || response.data.statusCode !== 200) {
      throw new Error(response.data.message || 'Failed to update two-factor authentication');
    }
    
    toast.success(response.data.message || `Two-factor authentication ${enable ? 'enabled' : 'disabled'} successfully!`);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating two-factor authentication:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Failed to update two-factor authentication');
    } else {
      toast.error('Failed to update two-factor authentication. Please try again.');
    }
    
    throw error;
  }
};